import { isPartnerPackageProduct } from "common";
import { Package, PartnerPackage } from "types";
import { useProduct } from "./resources/useProduct";
import { useResource } from "./useResource";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface UsePackageOptions {
    isPartnerPackage?: boolean;
    enabledForRegular?: boolean;
    enabledForPartner?: boolean;
}

export const usePackage = (packageId: string | null | undefined, options: UsePackageOptions = {}) => {
    const { isPartnerPackage, enabledForRegular = true, enabledForPartner = true } = options;
    // Purchase can be unavailable in the store, so we need to check if it is available
    const firstItem = useCreatePurchaseStore((state) => state.getFirstItem());
    const storeIsPartnerPurchase = useCreatePurchaseStore((state) => state.isPartnerPurchase());
    // We'll use product endpoint only if purchase is unavailable in the store
    const shouldUseProduct = !firstItem && isPartnerPackage === undefined;
    const { product } = useProduct(packageId ?? undefined, null, {
        enabled: shouldUseProduct,
    });

    // By default, isPartnerBooking = isPartnerPackage if specified
    // If still undefined, we deduce it below
    let isPartnerBooking = isPartnerPackage;

    if (isPartnerBooking === undefined) {
        isPartnerBooking = firstItem ? storeIsPartnerPurchase : isPartnerPackageProduct(product);
    }

    const { data: regularPackage } = useResource<Package>(["packages", packageId], null, {
        enabled: !!packageId && !isPartnerBooking && !shouldUseProduct && enabledForRegular,
    });

    const { data: partnerPackage } = useResource<PartnerPackage>(["partnerPackages", packageId], null, {
        enabled: !!packageId && isPartnerBooking && !shouldUseProduct && enabledForPartner,
    });

    return {
        pkg: shouldUseProduct ? (product as PartnerPackage | Package) : regularPackage ?? partnerPackage,
        isPartnerBooking,
    };
};
